/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase 15.4 — Vehicle Operations (Maintenance · Challans · Inspections)
 * Admin console with 3 tabs + dashboards.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Wrench,
  AlertOctagon,
  ClipboardCheck,
  Plus,
  Calendar,
  TrendingUp,
  Truck,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Upload as UploadIcon,
  FileSignature,
  Camera,
  Video,
  AlertTriangle,
  Star,
} from "lucide-react";
import { formatINR } from "@/lib/format";

const MAINT_TYPES = {
  preventive: "Preventive Service",
  repair: "Repair",
  breakdown: "Breakdown",
  tyre: "Tyre Change / Rotation",
  oil: "Oil / Lubricant",
  battery: "Battery",
  bodywork: "Bodywork / Paint",
  ac: "A/C / HVAC",
  electrical: "Electrical",
  other: "Other",
};

const CHALLAN_STATUS = {
  pending: { label: "Pending", cls: "bg-red-100 text-red-800" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800" },
  contested: { label: "Contested", cls: "bg-amber-100 text-amber-800" },
  waived: { label: "Waived", cls: "bg-slate-200 text-slate-700" },
};

const INSP_TYPES = {
  pre_trip: "Pre-Trip",
  post_trip: "Post-Trip",
  weekly: "Weekly",
  monthly: "Monthly",
  ad_hoc: "Ad-hoc",
};

const OVERALL_TONE = {
  pass: "bg-emerald-100 text-emerald-800",
  fail: "bg-red-100 text-red-800",
  conditional: "bg-amber-100 text-amber-800",
};

export default function VehicleOps() {
  const [vehicles, setVehicles] = useState([]);
  const [tab, setTab] = useState("maintenance");

  useEffect(() => {
    api
      .get("/logistics/trucks")
      .then(({ data }) => setVehicles(data || []))
      .catch(() => toast.error("Could not load vehicles"));
  }, []);

  return (
    <div className="space-y-6" data-testid="vehicle-ops-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 mb-1">
          <Truck className="h-3.5 w-3.5" /> Phase 15.4 · Driver Portal v3
        </div>
        <h1
          className="text-2xl font-bold text-slate-900"
          style={{ fontFamily: "Chivo" }}
        >
          Vehicle Operations
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Maintenance logs, traffic challans, and pre/post-trip inspections
          across the fleet.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-3xl">
          <TabsTrigger value="maintenance" data-testid="vops-tab-maintenance">
            <Wrench className="h-3.5 w-3.5 mr-1.5" /> Maintenance
          </TabsTrigger>
          <TabsTrigger value="challans" data-testid="vops-tab-challans">
            <AlertOctagon className="h-3.5 w-3.5 mr-1.5" /> Challans
          </TabsTrigger>
          <TabsTrigger value="inspections" data-testid="vops-tab-inspections">
            <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" /> Inspections
          </TabsTrigger>
          <TabsTrigger value="handover" data-testid="vops-tab-handover">
            <FileSignature className="h-3.5 w-3.5 mr-1.5" /> Handover
          </TabsTrigger>
        </TabsList>

        <TabsContent value="maintenance" className="mt-4">
          <MaintenanceTab vehicles={vehicles} />
        </TabsContent>
        <TabsContent value="challans" className="mt-4">
          <ChallansTab vehicles={vehicles} />
        </TabsContent>
        <TabsContent value="inspections" className="mt-4">
          <InspectionsTab vehicles={vehicles} />
        </TabsContent>
        <TabsContent value="handover" className="mt-4">
          <HandoverTab vehicles={vehicles} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ================================================================== */
/*                           MAINTENANCE                              */
/* ================================================================== */
function MaintenanceTab({ vehicles }) {
  const [dash, setDash] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterVid, setFilterVid] = useState("all");
  const [dlg, setDlg] = useState(false);
  const blankForm = {
    vehicle_id: "",
    date: new Date().toISOString().slice(0, 10),
    maint_type: "preventive",
    cost: "",
    odometer_km: "",
    vendor: "",
    invoice_no: "",
    description: "",
    next_due_km: "",
    next_due_date: "",
    status: "completed",
    paid_by: "company",
  };
  const [form, setForm] = useState(blankForm);
  const [billFile, setBillFile] = useState(null);
  const [paymentFile, setPaymentFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [d, u] = await Promise.all([
        api.get("/vehicle-ops/maintenance/dashboard"),
        api.get("/vehicle-ops/maintenance/upcoming?days=30"),
      ]);
      setDash(d.data);
      setUpcoming(u.data || []);
      // Fleet-wide list = aggregate by querying upcoming + add filter for vehicle
      if (filterVid !== "all") {
        const r = await api.get(`/vehicle-ops/vehicles/${filterVid}/maintenance`);
        setRows(r.data || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => {
    refresh();
  }, [filterVid]);

  const submit = async () => {
    if (!form.vehicle_id) {
      toast.error("Select vehicle");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        cost: parseFloat(form.cost || 0),
        odometer_km: form.odometer_km ? parseFloat(form.odometer_km) : null,
        next_due_km: form.next_due_km ? parseFloat(form.next_due_km) : null,
        next_due_date: form.next_due_date || null,
      };
      const { data } = await api.post(
        `/vehicle-ops/vehicles/${form.vehicle_id}/maintenance`,
        payload,
      );
      const mid = data.id;
      // Optional bill upload
      if (billFile) {
        try {
          const fd = new FormData();
          fd.append("file", billFile);
          await api.post(`/vehicle-ops/maintenance/${mid}/upload-bill`, fd);
        } catch (e) {
          toast.warning("Saved, but bill upload failed");
        }
      }
      // Optional payment proof upload
      if (paymentFile) {
        try {
          const fd = new FormData();
          fd.append("file", paymentFile);
          await api.post(`/vehicle-ops/maintenance/${mid}/upload-payment`, fd);
        } catch (e) {
          toast.warning("Saved, but payment proof upload failed");
        }
      }
      toast.success("Maintenance logged");
      setDlg(false);
      setForm(blankForm);
      setBillFile(null);
      setPaymentFile(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this maintenance entry?")) return;
    try {
      await api.delete(`/vehicle-ops/maintenance/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="maintenance-tab">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total Logs" value={dash?.total ?? "—"} tone="slate" />
        <KpiTile label="Overdue" value={dash?.overdue ?? 0} tone="rose" />
        <KpiTile label="Due in 30d" value={dash?.due_soon_30d ?? 0} tone="amber" />
        <KpiTile
          label="Cost YTD"
          value={dash?.cost_ytd ? formatINR(dash.cost_ytd) : "₹0"}
          tone="emerald"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterVid} onValueChange={setFilterVid}>
          <SelectTrigger className="w-72" data-testid="maint-filter-vehicle">
            <SelectValue placeholder="Filter vehicle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vehicles (upcoming only)</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.truck_no} · {v.make || ""} {v.model || ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          data-testid="maint-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <div className="flex-1" />
        <Button
          onClick={() => setDlg(true)}
          data-testid="maint-add-btn"
          className="bg-bravura hover:bg-bravura-700"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Log Maintenance
        </Button>
      </div>

      {/* Upcoming list (default) or vehicle-specific rows */}
      <Card>
        <CardContent className="p-0">
          {filterVid === "all" ? (
            <Section title={`Upcoming / Overdue (${upcoming.length})`}>
              {upcoming.length === 0 ? (
                <Empty msg="Nothing due in the next 30 days." />
              ) : (
                <MaintTable rows={upcoming} showVehicle onDelete={remove} />
              )}
            </Section>
          ) : (
            <Section title={`Maintenance History (${rows.length})`}>
              {rows.length === 0 ? (
                <Empty msg="No maintenance logs for this vehicle." />
              ) : (
                <MaintTable rows={rows} onDelete={remove} />
              )}
            </Section>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by type */}
      {dash?.by_type?.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4 text-emerald-600" /> Cost
              breakdown by category
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {dash.by_type.map((t) => (
                <div
                  key={t.type}
                  className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2"
                >
                  <div>
                    <div className="text-xs font-semibold text-slate-700">
                      {t.label}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {t.count} entries
                    </div>
                  </div>
                  <div className="text-sm font-bold text-slate-900">
                    {formatINR(t.total_cost)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Maintenance</DialogTitle>
            <DialogDescription>
              Track service, repair, tyre, breakdown, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vehicle *">
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
              >
                <SelectTrigger data-testid="maint-form-vehicle">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.truck_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date *">
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="maint-form-date"
              />
            </Field>
            <Field label="Type *">
              <Select
                value={form.maint_type}
                onValueChange={(v) => setForm({ ...form, maint_type: v })}
              >
                <SelectTrigger data-testid="maint-form-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(MAINT_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Total Amount ₹">
              <Input
                type="number"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                data-testid="maint-form-cost"
                placeholder="0"
              />
            </Field>
            <Field label="Paid By">
              <Select
                value={form.paid_by}
                onValueChange={(v) => setForm({ ...form, paid_by: v })}
              >
                <SelectTrigger data-testid="maint-form-paid-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Driver</SelectItem>
                  <SelectItem value="logistics_manager">
                    Logistics Manager
                  </SelectItem>
                  <SelectItem value="accounts">Accounts</SelectItem>
                  <SelectItem value="company">Company (direct)</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Odometer (km)">
              <Input
                type="number"
                value={form.odometer_km}
                onChange={(e) =>
                  setForm({ ...form, odometer_km: e.target.value })
                }
              />
            </Field>
            <Field label="Vendor / Garage">
              <Input
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              />
            </Field>
            <Field label="Invoice No.">
              <Input
                value={form.invoice_no}
                onChange={(e) =>
                  setForm({ ...form, invoice_no: e.target.value })
                }
              />
            </Field>
            <Field label="Next due (km)">
              <Input
                type="number"
                value={form.next_due_km}
                onChange={(e) =>
                  setForm({ ...form, next_due_km: e.target.value })
                }
              />
            </Field>
            <Field label="Next due date">
              <Input
                type="date"
                value={form.next_due_date}
                onChange={(e) =>
                  setForm({ ...form, next_due_date: e.target.value })
                }
              />
            </Field>
            <div className="col-span-2">
              <Field label="Description / Parts">
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  data-testid="maint-form-desc"
                />
              </Field>
            </div>
            {/* File attachments */}
            <div className="col-span-2 border-t border-slate-200 pt-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                <UploadIcon className="h-3 w-3" /> Attachments (PDF / image,
                max 5 MB each)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[11px] text-slate-600">
                    Bill / Invoice
                  </span>
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setBillFile(e.target.files?.[0] || null)}
                    data-testid="maint-form-bill"
                    className="text-xs h-9"
                  />
                  {billFile && (
                    <div className="text-[10px] text-emerald-700 mt-1">
                      ✓ {billFile.name}
                    </div>
                  )}
                </label>
                <label className="block">
                  <span className="text-[11px] text-slate-600">
                    Payment Proof (UPI / receipt)
                  </span>
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) =>
                      setPaymentFile(e.target.files?.[0] || null)
                    }
                    data-testid="maint-form-payment"
                    className="text-xs h-9"
                  />
                  {paymentFile && (
                    <div className="text-[10px] text-emerald-700 mt-1">
                      ✓ {paymentFile.name}
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              data-testid="maint-form-save"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MaintTable({ rows, showVehicle, onDelete }) {
  const PAID_LABEL = {
    driver: "Driver",
    logistics_manager: "LM",
    accounts: "Accounts",
    company: "Co.",
    pending: "Pending",
  };
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-slate-50">
          <tr className="text-[10px] uppercase text-slate-500">
            <th className="text-left px-3 py-2">Date</th>
            {showVehicle && <th className="text-left px-3 py-2">Vehicle</th>}
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-right px-3 py-2">Odo (km)</th>
            <th className="text-right px-3 py-2">Amount</th>
            <th className="text-left px-3 py-2">Paid By</th>
            <th className="text-left px-3 py-2">Files</th>
            <th className="text-left px-3 py-2">Next Due</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="px-3 py-2">{r.date}</td>
              {showVehicle && (
                <td className="px-3 py-2 font-semibold">
                  {r.truck_no || "—"}
                </td>
              )}
              <td className="px-3 py-2">
                <Badge variant="outline" className="text-[10px]">
                  {MAINT_TYPES[r.maint_type] || r.maint_type}
                </Badge>
              </td>
              <td className="px-3 py-2 max-w-xs truncate">
                {r.description || "—"}
              </td>
              <td className="px-3 py-2 text-right">
                {r.odometer_km ? r.odometer_km.toLocaleString("en-IN") : "—"}
              </td>
              <td className="px-3 py-2 text-right font-semibold">
                {formatINR(r.cost || 0)}
              </td>
              <td className="px-3 py-2">
                {r.paid_by ? (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      r.paid_by === "pending"
                        ? "bg-amber-100 text-amber-800"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {PAID_LABEL[r.paid_by] || r.paid_by}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-3 py-2">
                <div className="flex gap-1 items-center">
                  {r.file_url ? (
                    <a
                      href={r.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded hover:bg-sky-200"
                      title={r.file_name || "Bill"}
                    >
                      📄 Bill
                    </a>
                  ) : null}
                  {r.payment_file_url ? (
                    <a
                      href={r.payment_file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded hover:bg-emerald-200"
                      title={r.payment_file_name || "Payment"}
                    >
                      ✓ Pay
                    </a>
                  ) : null}
                  {!r.file_url && !r.payment_file_url && (
                    <span className="text-slate-300">—</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2">
                {r.next_due_date ? (
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      r.days_left < 0
                        ? "bg-red-100 text-red-800"
                        : r.days_left <= 7
                        ? "bg-amber-100 text-amber-800"
                        : "bg-emerald-50 text-emerald-700"
                    }`}
                  >
                    {r.next_due_date}
                    {r.days_left != null && ` (${r.days_left}d)`}
                  </span>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  onClick={() => onDelete(r.id)}
                  className="text-red-600 text-[10px] hover:underline"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ================================================================== */
/*                            CHALLANS                                */
/* ================================================================== */
function ChallansTab({ vehicles }) {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVid, setFilterVid] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    challan_no: "",
    challan_date: new Date().toISOString().slice(0, 10),
    offence: "",
    amount: "",
    location: "",
    status: "pending",
    notes: "",
  });

  const refresh = async () => {
    try {
      const d = await api.get("/vehicle-ops/challans/dashboard");
      setDash(d.data);
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterVid !== "all") params.set("vehicle_id", filterVid);
      const r = await api.get(`/vehicle-ops/challans?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => {
    refresh();
  }, [filterStatus, filterVid]);

  const submit = async () => {
    if (!form.vehicle_id || !form.challan_no) {
      toast.error("Vehicle & challan no. required");
      return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0) };
      await api.post(`/vehicle-ops/vehicles/${form.vehicle_id}/challans`, payload);
      toast.success("Challan added");
      setDlg(false);
      setForm({
        vehicle_id: "",
        challan_no: "",
        challan_date: new Date().toISOString().slice(0, 10),
        offence: "",
        amount: "",
        location: "",
        status: "pending",
        notes: "",
      });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const markPaid = async (id) => {
    const ref = window.prompt("Payment reference / TXN ID? (optional)") || "";
    try {
      await api.patch(`/vehicle-ops/challans/${id}`, {
        status: "paid",
        payment_ref: ref,
      });
      toast.success("Marked paid");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this challan?")) return;
    try {
      await api.delete(`/vehicle-ops/challans/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const byStatus = dash?.by_status || {};

  return (
    <div className="space-y-4" data-testid="challans-tab">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Pending"
          value={dash?.pending_count ?? 0}
          tone="rose"
          sub={dash?.pending_amount ? formatINR(dash.pending_amount) : "₹0"}
        />
        <KpiTile
          label="Paid"
          value={byStatus.paid?.count || 0}
          tone="emerald"
          sub={byStatus.paid?.amount ? formatINR(byStatus.paid.amount) : "₹0"}
        />
        <KpiTile
          label="Contested"
          value={byStatus.contested?.count || 0}
          tone="amber"
        />
        <KpiTile
          label="Waived"
          value={byStatus.waived?.count || 0}
          tone="slate"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40" data-testid="challan-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(CHALLAN_STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterVid} onValueChange={setFilterVid}>
          <SelectTrigger className="w-64" data-testid="challan-filter-vehicle">
            <SelectValue placeholder="Vehicle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.truck_no}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <div className="flex-1" />
        <Button
          onClick={() => setDlg(true)}
          data-testid="challan-add-btn"
          className="bg-bravura hover:bg-bravura-700"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Add Challan
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <Empty msg="No challans recorded." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase text-slate-500">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-left px-3 py-2">Challan #</th>
                    <th className="text-left px-3 py-2">Offence</th>
                    <th className="text-left px-3 py-2">Location</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = CHALLAN_STATUS[r.status] || {
                      label: r.status,
                      cls: "bg-slate-100",
                    };
                    return (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{r.challan_date}</td>
                        <td className="px-3 py-2 font-semibold">
                          {r.truck_no || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px]">
                          {r.challan_no}
                        </td>
                        <td className="px-3 py-2 max-w-xs truncate">
                          {r.offence}
                        </td>
                        <td className="px-3 py-2">{r.location || "—"}</td>
                        <td className="px-3 py-2 text-right font-semibold">
                          {formatINR(r.amount)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}
                          >
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {r.status === "pending" && (
                            <button
                              onClick={() => markPaid(r.id)}
                              className="text-emerald-700 text-[10px] hover:underline mr-2"
                              data-testid={`challan-pay-${r.id}`}
                            >
                              Mark Paid
                            </button>
                          )}
                          <button
                            onClick={() => remove(r.id)}
                            className="text-red-600 text-[10px] hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top offenders */}
      {(dash?.top_vehicles_ytd?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-3">
              Top 5 vehicles (YTD)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {dash.top_vehicles_ytd.map((v) => (
                <div
                  key={v.vehicle_id}
                  className="rounded-lg bg-red-50 px-3 py-2"
                >
                  <div className="text-sm font-bold text-red-900">
                    {v.truck_no}
                  </div>
                  <div className="text-[10px] text-red-700">
                    {v.count} challans · {formatINR(v.amount)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Traffic Challan</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vehicle *">
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
              >
                <SelectTrigger data-testid="challan-form-vehicle">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.truck_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Challan No. *">
              <Input
                value={form.challan_no}
                onChange={(e) =>
                  setForm({ ...form, challan_no: e.target.value })
                }
                data-testid="challan-form-no"
              />
            </Field>
            <Field label="Challan Date *">
              <Input
                type="date"
                value={form.challan_date}
                onChange={(e) =>
                  setForm({ ...form, challan_date: e.target.value })
                }
              />
            </Field>
            <Field label="Amount ₹ *">
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="challan-form-amount"
              />
            </Field>
            <div className="col-span-2">
              <Field label="Offence *">
                <Input
                  value={form.offence}
                  onChange={(e) =>
                    setForm({ ...form, offence: e.target.value })
                  }
                  data-testid="challan-form-offence"
                  placeholder="Overspeeding / Overloading / Wrong-lane …"
                />
              </Field>
            </div>
            <Field label="Location">
              <Input
                value={form.location}
                onChange={(e) =>
                  setForm({ ...form, location: e.target.value })
                }
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CHALLAN_STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Notes">
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) =>
                    setForm({ ...form, notes: e.target.value })
                  }
                />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>
              Cancel
            </Button>
            <Button onClick={submit} data-testid="challan-form-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================================================================== */
/*                           INSPECTIONS                              */
/* ================================================================== */
function InspectionsTab({ vehicles }) {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterVid, setFilterVid] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [template, setTemplate] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: "",
    insp_type: "pre_trip",
    date: new Date().toISOString().slice(0, 10),
    odometer_km: "",
    fuel_level: "",
    items: [],
    defects_found: "",
    action_required: "",
  });
  const [viewInsp, setViewInsp] = useState(null);

  const refresh = async () => {
    try {
      const d = await api.get("/vehicle-ops/inspections/dashboard?days=30");
      setDash(d.data);
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("insp_type", filterType);
      if (filterVid !== "all") params.set("vehicle_id", filterVid);
      const r = await api.get(`/vehicle-ops/inspections?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => {
    refresh();
  }, [filterType, filterVid]);

  const openDialog = async () => {
    try {
      const t = await api.get(
        `/vehicle-ops/inspections/template?insp_type=${form.insp_type}`,
      );
      setTemplate(t.data.items);
      setForm({
        ...form,
        items: t.data.items.map((it) => ({
          key: it.key,
          label: it.label,
          status: "pass",
          notes: "",
        })),
      });
      setDlg(true);
    } catch (e) {
      toast.error("Could not load template");
    }
  };

  const changeInspType = async (v) => {
    setForm({ ...form, insp_type: v });
    const t = await api.get(`/vehicle-ops/inspections/template?insp_type=${v}`);
    setTemplate(t.data.items);
    setForm((f) => ({
      ...f,
      insp_type: v,
      items: t.data.items.map((it) => ({
        key: it.key,
        label: it.label,
        status: "pass",
        notes: "",
      })),
    }));
  };

  const setItemStatus = (idx, st) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) =>
        i === idx ? { ...it, status: st } : it,
      ),
    }));
  };
  const setItemNotes = (idx, n) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, notes: n } : it)),
    }));
  };

  const submit = async () => {
    if (!form.vehicle_id) {
      toast.error("Select vehicle");
      return;
    }
    try {
      const payload = {
        insp_type: form.insp_type,
        date: form.date,
        odometer_km: form.odometer_km ? parseFloat(form.odometer_km) : null,
        fuel_level: form.fuel_level || null,
        items: form.items,
        defects_found: form.defects_found || null,
        action_required: form.action_required || null,
      };
      await api.post(
        `/vehicle-ops/vehicles/${form.vehicle_id}/inspections`,
        payload,
      );
      toast.success("Inspection saved");
      setDlg(false);
      setForm({
        vehicle_id: "",
        insp_type: "pre_trip",
        date: new Date().toISOString().slice(0, 10),
        odometer_km: "",
        fuel_level: "",
        items: [],
        defects_found: "",
        action_required: "",
      });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inspection?")) return;
    try {
      await api.delete(`/vehicle-ops/inspections/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="inspections-tab">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          label="Inspections (30d)"
          value={dash?.total ?? 0}
          tone="slate"
        />
        <KpiTile
          label="Pass Rate"
          value={`${dash?.pass_rate_pct ?? 0}%`}
          tone="emerald"
        />
        <KpiTile
          label="Fail / Conditional"
          value={
            (dash?.by_status?.fail || 0) + (dash?.by_status?.conditional || 0)
          }
          tone="rose"
        />
        <KpiTile
          label="Defect Reports"
          value={dash?.recent_defects?.length ?? 0}
          tone="amber"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44" data-testid="insp-filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(INSP_TYPES).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterVid} onValueChange={setFilterVid}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Vehicle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.truck_no}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <div className="flex-1" />
        <Button
          onClick={openDialog}
          data-testid="insp-add-btn"
          className="bg-bravura hover:bg-bravura-700"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Inspection
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <Empty msg="No inspections recorded." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase text-slate-500">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Inspector</th>
                    <th className="text-right px-3 py-2">Odo</th>
                    <th className="text-left px-3 py-2">Overall</th>
                    <th className="text-left px-3 py-2">Defects</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">{r.date}</td>
                      <td className="px-3 py-2 font-semibold">
                        {r.truck_no || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px]">
                          {INSP_TYPES[r.insp_type] || r.insp_type}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        {r.inspector_name || r.driver_name || "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.odometer_km
                          ? r.odometer_km.toLocaleString("en-IN")
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${
                            OVERALL_TONE[r.overall_status] ||
                            "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {r.overall_status}
                        </span>
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate">
                        {r.defects_found || "—"}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => setViewInsp(r)}
                          className="text-bravura-700 text-[10px] hover:underline mr-2"
                          data-testid={`insp-view-${r.id}`}
                        >
                          View
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          className="text-red-600 text-[10px] hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New inspection dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Inspection</DialogTitle>
            <DialogDescription>
              Mark each item Pass / Fail / Conditional. Overall verdict
              auto-computes.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Field label="Vehicle *">
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
              >
                <SelectTrigger data-testid="insp-form-vehicle">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.truck_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Type *">
              <Select value={form.insp_type} onValueChange={changeInspType}>
                <SelectTrigger data-testid="insp-form-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INSP_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date">
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </Field>
            <Field label="Odometer (km)">
              <Input
                type="number"
                value={form.odometer_km}
                onChange={(e) =>
                  setForm({ ...form, odometer_km: e.target.value })
                }
              />
            </Field>
            <Field label="Fuel level">
              <Select
                value={form.fuel_level}
                onValueChange={(v) => setForm({ ...form, fuel_level: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="F">Full</SelectItem>
                  <SelectItem value="3/4">3/4</SelectItem>
                  <SelectItem value="1/2">1/2</SelectItem>
                  <SelectItem value="1/4">1/4</SelectItem>
                  <SelectItem value="E">Empty</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <Label className="text-xs uppercase font-semibold text-slate-500">
              Checklist ({form.items.length} items)
            </Label>
            {form.items.map((it, idx) => (
              <div
                key={it.key}
                className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-lg p-2"
                data-testid={`insp-item-${it.key}`}
              >
                <div className="col-span-5 text-xs font-medium">
                  {it.label}
                </div>
                <div className="col-span-3">
                  <Select
                    value={it.status}
                    onValueChange={(v) => setItemStatus(idx, v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pass">Pass</SelectItem>
                      <SelectItem value="conditional">Conditional</SelectItem>
                      <SelectItem value="fail">Fail</SelectItem>
                      <SelectItem value="na">N/A</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  <Input
                    placeholder="Notes (optional)"
                    value={it.notes}
                    onChange={(e) => setItemNotes(idx, e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <Field label="Overall defects found">
              <Textarea
                rows={2}
                value={form.defects_found}
                onChange={(e) =>
                  setForm({ ...form, defects_found: e.target.value })
                }
                placeholder="Describe any major issues observed"
              />
            </Field>
            <Field label="Action required">
              <Textarea
                rows={2}
                value={form.action_required}
                onChange={(e) =>
                  setForm({ ...form, action_required: e.target.value })
                }
                placeholder="E.g. Schedule brake-pad replacement"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>
              Cancel
            </Button>
            <Button onClick={submit} data-testid="insp-form-save">
              Save Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewInsp} onOpenChange={(o) => !o && setViewInsp(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {INSP_TYPES[viewInsp?.insp_type] || viewInsp?.insp_type} ·{" "}
              {viewInsp?.truck_no}
            </DialogTitle>
            <DialogDescription>
              {viewInsp?.date} · Inspector{" "}
              {viewInsp?.inspector_name || "—"} · Odo{" "}
              {viewInsp?.odometer_km
                ? viewInsp.odometer_km.toLocaleString("en-IN")
                : "—"}
            </DialogDescription>
          </DialogHeader>
          {viewInsp && (
            <div className="space-y-3">
              <div>
                <span
                  className={`text-[11px] px-2 py-0.5 rounded uppercase font-semibold ${
                    OVERALL_TONE[viewInsp.overall_status] ||
                    "bg-slate-100"
                  }`}
                >
                  Overall: {viewInsp.overall_status}
                </span>
              </div>
              <div className="border rounded-lg divide-y divide-slate-100">
                {(viewInsp.items || []).map((it, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2"
                  >
                    <div>
                      <div className="text-xs font-semibold">
                        {it.label || it.key}
                      </div>
                      {it.notes && (
                        <div className="text-[10px] text-slate-500">
                          {it.notes}
                        </div>
                      )}
                    </div>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
                        it.status === "pass"
                          ? "bg-emerald-100 text-emerald-800"
                          : it.status === "conditional"
                          ? "bg-amber-100 text-amber-800"
                          : it.status === "fail"
                          ? "bg-red-100 text-red-800"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {it.status}
                    </span>
                  </div>
                ))}
              </div>
              {viewInsp.defects_found && (
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
                    Defects Found
                  </div>
                  <div className="text-xs">{viewInsp.defects_found}</div>
                </div>
              )}
              {viewInsp.action_required && (
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
                    Action Required
                  </div>
                  <div className="text-xs">{viewInsp.action_required}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================================================================== */
/*                            HELPERS                                 */
/* ================================================================== */
function KpiTile({ label, value, tone, sub }) {
  const tones = {
    slate: "bg-slate-50 text-slate-900",
    rose: "bg-red-50 text-red-900",
    amber: "bg-amber-50 text-amber-900",
    emerald: "bg-emerald-50 text-emerald-900",
  };
  return (
    <div className={`rounded-xl p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">
        {label}
      </div>
      <div className="text-xl font-bold mt-0.5" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {title}
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="px-4 py-10 text-center text-xs text-slate-400">{msg}</div>
  );
}


/* ================================================================== */
/*                            HANDOVER                                */
/* ================================================================== */
function HandoverTab({ vehicles }) {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVid, setFilterVid] = useState("all");
  const [drivers, setDrivers] = useState([]);
  const [template, setTemplate] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewHO, setViewHO] = useState(null);
  const [createdId, setCreatedId] = useState(null);

  const blank = {
    vehicle_id: "",
    driver_id: "",
    handover_type: "assignment",
    handover_date: new Date().toISOString().slice(0, 10),
    odometer_km: "",
    items: [],
    critical_notes: "",
    manager_signature_name: "",
  };
  const [form, setForm] = useState(blank);

  // Load drivers list once
  useEffect(() => {
    api
      .get("/employees?category=driver&limit=200")
      .then(({ data }) =>
        setDrivers(Array.isArray(data) ? data : data.items || []),
      )
      .catch((e) => console.warn("Drivers load failed:", e?.message));
    api
      .get("/vehicle-ops/handovers/template")
      .then(({ data }) => setTemplate(data.items || []))
      .catch((e) => console.warn("Template load failed:", e?.message));
  }, []);

  const refresh = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterVid !== "all") params.set("vehicle_id", filterVid);
      const [d, r] = await Promise.all([
        api.get("/vehicle-ops/handovers/dashboard"),
        api.get(`/vehicle-ops/handovers/?${params.toString()}`),
      ]);
      setDash(d.data);
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  useEffect(() => {
    refresh();
  }, [filterStatus, filterVid]);

  const openCreate = async () => {
    // If template hasn't loaded yet (or failed silently earlier), fetch it now
    let itemsTpl = template;
    if (!itemsTpl || itemsTpl.length === 0) {
      try {
        const { data } = await api.get("/vehicle-ops/handovers/template");
        itemsTpl = data.items || [];
        setTemplate(itemsTpl);
      } catch (e) {
        toast.error("Could not load handover checklist template");
        return;
      }
    }
    // Also ensure drivers list (silently retry if empty)
    if (drivers.length === 0) {
      try {
        const { data } = await api.get(
          "/employees?category=driver&limit=200",
        );
        setDrivers(Array.isArray(data) ? data : data.items || []);
      } catch (e) {
        /* non-fatal */
      }
    }
    setForm({
      ...blank,
      items: itemsTpl.map((t) => ({
        key: t.key,
        label: t.label,
        critical: t.critical,
        optional: t.optional,
        status: t.optional ? "na" : "present",
        notes: "",
      })),
    });
    setCreatedId(null);
    setDlg(true);
  };

  const setItemStatus = (idx, st) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) =>
        i === idx ? { ...it, status: st } : it,
      ),
    }));
  };
  const setItemNotes = (idx, n) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, notes: n } : it)),
    }));
  };

  const submit = async () => {
    if (!form.vehicle_id || !form.driver_id) {
      toast.error("Vehicle and driver required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        driver_id: form.driver_id,
        handover_type: form.handover_type,
        handover_date: form.handover_date,
        odometer_km: form.odometer_km ? parseFloat(form.odometer_km) : null,
        items: form.items.map((it) => ({
          key: it.key,
          status: it.status,
          notes: it.notes,
        })),
        critical_notes: form.critical_notes || null,
        manager_signature_name: form.manager_signature_name || null,
        complete_now: true,
      };
      const { data } = await api.post("/vehicle-ops/handovers/", payload);
      toast.success("Handover signed by manager — awaiting driver ack");
      setCreatedId(data.id);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadItemPhoto = async (hid, key, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vehicle-ops/handovers/${hid}/items/${key}/photo`, fd);
      toast.success(`Photo attached`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const uploadVideo = async (hid, file) => {
    if (!file) return;
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > 12) {
      toast.error(`Video is ${sizeMB.toFixed(1)} MB — max 12 MB`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vehicle-ops/handovers/${hid}/walkthrough-video`, fd);
      toast.success("Walkthrough video uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this handover?")) return;
    try {
      await api.delete(`/vehicle-ops/handovers/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const STATUS_LABEL = {
    draft: { label: "Draft", cls: "bg-slate-200 text-slate-700" },
    pending_driver_ack: { label: "Pending Driver Ack", cls: "bg-amber-100 text-amber-800" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800" },
  };

  return (
    <div className="space-y-4" data-testid="handover-tab">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Total Handovers" value={dash?.total ?? 0} tone="slate" />
        <KpiTile
          label="Pending Driver Ack"
          value={dash?.pending_driver_ack ?? 0}
          tone="amber"
        />
        <KpiTile
          label="Completed"
          value={dash?.completed ?? 0}
          tone="emerald"
        />
        <KpiTile
          label="With Missing Critical"
          value={dash?.recent_with_missing_critical?.length ?? 0}
          tone="rose"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-52" data-testid="ho-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_driver_ack">Pending Driver Ack</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterVid} onValueChange={setFilterVid}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Vehicle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vehicles</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.truck_no}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
        <div className="flex-1" />
        <Button
          onClick={openCreate}
          data-testid="ho-add-btn"
          className="bg-bravura hover:bg-bravura-700"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Handover
        </Button>
      </div>

      {/* Alerts: missing critical */}
      {(dash?.recent_with_missing_critical?.length ?? 0) > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-900 font-bold text-sm mb-2">
              <AlertTriangle className="h-4 w-4" /> Recent handovers with
              missing / damaged critical items
            </div>
            <div className="space-y-1">
              {dash.recent_with_missing_critical.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center justify-between text-xs"
                >
                  <div>
                    <span className="font-semibold">{h.truck_no}</span> ·{" "}
                    {h.driver_name || "—"} · {h.handover_date}
                  </div>
                  <div className="text-red-700">
                    {(h.missing_critical || []).join(", ")}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <Empty msg="No handovers recorded." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase text-slate-500">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Vehicle</th>
                    <th className="text-left px-3 py-2">Driver</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Manager</th>
                    <th className="text-left px-3 py-2">Driver Ack</th>
                    <th className="text-left px-3 py-2">Critical</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = STATUS_LABEL[r.status] || {
                      label: r.status,
                      cls: "bg-slate-100",
                    };
                    return (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{r.handover_date}</td>
                        <td className="px-3 py-2 font-semibold">
                          {r.truck_no || "—"}
                        </td>
                        <td className="px-3 py-2">
                          {r.driver_name || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px]">
                            {r.handover_type === "assignment"
                              ? "Assignment"
                              : "Monthly"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-[10px]">
                          {r.manager_signature_name || r.manager_name}
                        </td>
                        <td className="px-3 py-2 text-[10px]">
                          {r.driver_signature_name ? (
                            <span className="text-emerald-700">
                              ✓ {r.driver_signature_name}
                            </span>
                          ) : (
                            <span className="text-amber-700">Pending</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {(r.missing_critical || []).length > 0 ? (
                            <span className="text-red-700 font-semibold text-[10px]">
                              ⚠ {r.missing_critical.length} missing
                            </span>
                          ) : (
                            <span className="text-emerald-700 text-[10px]">
                              ✓ OK
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}
                          >
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => setViewHO(r)}
                            className="text-bravura-700 text-[10px] hover:underline mr-2"
                            data-testid={`ho-view-${r.id}`}
                          >
                            View
                          </button>
                          {r.status !== "completed" && (
                            <button
                              onClick={() => remove(r.id)}
                              className="text-red-600 text-[10px] hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {createdId ? "Attach Photos & Video" : "New Vehicle Handover"}
            </DialogTitle>
            <DialogDescription>
              {createdId
                ? "Handover signed by manager. Attach per-item photos and the outer-body walkthrough video."
                : "22-point checklist. Critical items (★) need extra attention."}
            </DialogDescription>
          </DialogHeader>

          {!createdId ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vehicle *">
                  <Select
                    value={form.vehicle_id}
                    onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
                  >
                    <SelectTrigger data-testid="ho-form-vehicle">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.truck_no}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Driver *">
                  <Select
                    value={form.driver_id}
                    onValueChange={(v) => setForm({ ...form, driver_id: v })}
                  >
                    <SelectTrigger data-testid="ho-form-driver">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name} ({d.employee_code || "—"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Type">
                  <Select
                    value={form.handover_type}
                    onValueChange={(v) =>
                      setForm({ ...form, handover_type: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="assignment">Fresh Assignment</SelectItem>
                      <SelectItem value="monthly">Monthly Re-verify</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Handover Date">
                  <Input
                    type="date"
                    value={form.handover_date}
                    onChange={(e) =>
                      setForm({ ...form, handover_date: e.target.value })
                    }
                  />
                </Field>
                <Field label="Odometer (km)">
                  <Input
                    type="number"
                    value={form.odometer_km}
                    onChange={(e) =>
                      setForm({ ...form, odometer_km: e.target.value })
                    }
                  />
                </Field>
                <Field label="Manager signature name (override)">
                  <Input
                    placeholder="Defaults to you"
                    value={form.manager_signature_name}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        manager_signature_name: e.target.value,
                      })
                    }
                  />
                </Field>
              </div>

              {/* 22-item checklist */}
              <div className="border-t border-slate-200 mt-4 pt-3">
                <Label className="text-xs uppercase font-semibold text-slate-500">
                  Checklist ({form.items.length} items)
                </Label>
                <div className="space-y-1.5 mt-2">
                  {form.items.map((it, idx) => (
                    <div
                      key={it.key}
                      className={`grid grid-cols-12 gap-2 items-center rounded-lg p-2 ${
                        it.critical
                          ? "bg-amber-50 ring-1 ring-amber-200"
                          : "bg-slate-50"
                      }`}
                      data-testid={`ho-item-${it.key}`}
                    >
                      <div className="col-span-5 text-xs font-medium flex items-center gap-1.5">
                        {it.critical && (
                          <Star className="h-3 w-3 text-amber-600 fill-amber-400" />
                        )}
                        {it.label}
                        {it.optional && (
                          <span className="text-[9px] text-slate-400">
                            (if required)
                          </span>
                        )}
                      </div>
                      <div className="col-span-3">
                        <Select
                          value={it.status}
                          onValueChange={(v) => setItemStatus(idx, v)}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="present">Present</SelectItem>
                            <SelectItem value="missing">Missing</SelectItem>
                            <SelectItem value="damaged">Damaged</SelectItem>
                            <SelectItem value="na">N/A</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Input
                          placeholder="Notes (optional)"
                          value={it.notes || ""}
                          onChange={(e) => setItemNotes(idx, e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 mt-4 pt-3">
                <Field label="Critical items notes">
                  <Textarea
                    rows={2}
                    placeholder="Fire extinguisher pressure / Tarpaulin condition / etc."
                    value={form.critical_notes}
                    onChange={(e) =>
                      setForm({ ...form, critical_notes: e.target.value })
                    }
                  />
                </Field>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDlg(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={busy}
                  data-testid="ho-form-save"
                >
                  {busy ? "Saving…" : "Sign & Save"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <AttachMedia
              hid={createdId}
              items={form.items}
              onUploadItem={(key, f) => uploadItemPhoto(createdId, key, f)}
              onUploadVideo={(f) => uploadVideo(createdId, f)}
              onClose={() => {
                setCreatedId(null);
                setDlg(false);
                setForm(blank);
                refresh();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewHO} onOpenChange={(o) => !o && setViewHO(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Handover · {viewHO?.truck_no}</DialogTitle>
            <DialogDescription>
              {viewHO?.handover_date} ·{" "}
              {viewHO?.handover_type === "assignment"
                ? "Fresh Assignment"
                : "Monthly Re-verify"}{" "}
              · Driver {viewHO?.driver_name || "—"}
            </DialogDescription>
          </DialogHeader>
          {viewHO && <HandoverViewer hid={viewHO.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AttachMedia({ hid, items, onUploadItem, onUploadVideo, onClose }) {
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-sky-50 ring-1 ring-sky-200 p-3 text-xs text-sky-900">
        <Video className="h-4 w-4 inline mr-1" />
        <strong>Outer-body walkthrough video</strong> (max 12 MB — record a
        slow 30-second walk-around showing all 4 sides + cabin)
        <div className="mt-2">
          <Input
            type="file"
            accept="video/*"
            capture="environment"
            onChange={(e) => onUploadVideo(e.target.files?.[0])}
            data-testid="ho-walkthrough-video"
          />
        </div>
      </div>
      <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
        {items.map((it) => (
          <div
            key={it.key}
            className="flex items-center gap-2 bg-slate-50 rounded-lg p-2"
          >
            <div className="flex-1 text-xs">
              {it.critical && (
                <Star className="h-3 w-3 inline text-amber-500 fill-amber-400 mr-1" />
              )}
              {it.label} —{" "}
              <span
                className={
                  it.status === "missing" || it.status === "damaged"
                    ? "text-red-700 font-semibold"
                    : "text-slate-500"
                }
              >
                {it.status}
              </span>
            </div>
            <label className="cursor-pointer text-bravura-700 text-[11px] flex items-center gap-1 hover:underline">
              <Camera className="h-3.5 w-3.5" /> Photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => onUploadItem(it.key, e.target.files?.[0])}
                data-testid={`ho-photo-${it.key}`}
              />
            </label>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button onClick={onClose} data-testid="ho-attach-done">
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

function HandoverViewer({ hid }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    api
      .get(`/vehicle-ops/handovers/${hid}`)
      .then(({ data }) => setDoc(data))
      .catch(() => {});
  }, [hid]);
  if (!doc) return <div className="text-xs text-slate-400">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">
            Manager Signature
          </div>
          <div className="font-semibold">{doc.manager_signature_name}</div>
          <div className="text-[10px] text-slate-500">
            {doc.manager_signed_at?.slice(0, 19).replace("T", " ")}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">
            Driver Acknowledgement
          </div>
          {doc.driver_signature_name ? (
            <>
              <div className="font-semibold text-emerald-700">
                {doc.driver_signature_name}
              </div>
              <div className="text-[10px] text-slate-500">
                {doc.driver_acknowledged_at?.slice(0, 19).replace("T", " ")}
              </div>
            </>
          ) : (
            <div className="text-amber-700 text-xs">Awaiting driver</div>
          )}
        </div>
        {doc.odometer_km && (
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold">
              Odometer
            </div>
            <div className="font-semibold">
              {doc.odometer_km.toLocaleString("en-IN")} km
            </div>
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold">
            Completion
          </div>
          <div className="font-semibold">{doc.completion_pct}%</div>
        </div>
      </div>

      {doc.walkthrough_video_url ? (
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
            Walkthrough Video
          </div>
          <video
            controls
            src={doc.walkthrough_video_url}
            className="rounded-lg max-h-72 w-full bg-black"
            data-testid="ho-view-video"
          />
        </div>
      ) : (
        <div className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 text-xs text-slate-500 flex items-center gap-2">
          <Video className="h-4 w-4 text-slate-400" />
          No walkthrough video uploaded for this handover.
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
          Items ({doc.items?.length || 0})
        </div>
        <div className="border rounded-lg divide-y divide-slate-100">
          {(doc.items || []).map((it, i) => (
            <div
              key={i}
              className="flex items-center justify-between px-3 py-2"
            >
              <div className="flex-1">
                <div className="text-xs font-semibold flex items-center gap-1">
                  {it.critical && (
                    <Star className="h-3 w-3 text-amber-500 fill-amber-400" />
                  )}
                  {it.label}
                </div>
                {it.notes && (
                  <div className="text-[10px] text-slate-500">{it.notes}</div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {it.photo_url && (
                  <img
                    src={it.photo_url}
                    alt=""
                    className="h-10 w-10 rounded object-cover ring-1 ring-slate-200"
                  />
                )}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
                    it.status === "present"
                      ? "bg-emerald-100 text-emerald-800"
                      : it.status === "missing"
                      ? "bg-red-100 text-red-800"
                      : it.status === "damaged"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {it.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {doc.critical_notes && (
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
            Critical Notes
          </div>
          <div className="text-xs bg-amber-50 rounded p-2">
            {doc.critical_notes}
          </div>
        </div>
      )}

      {doc.driver_ack_remarks && (
        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
            Driver Remarks
          </div>
          <div className="text-xs">{doc.driver_ack_remarks}</div>
        </div>
      )}
    </div>
  );
}
